import React, { useEffect, useState } from 'react';
import { labIncidentsService } from '../services/labIncidents';
import type { LabReport, LabReportDetail, RetestIncident } from '../types';
import { analyteLabel } from '../utils/labLabels';
import { unitLabel } from '../utils/presentation';

export const RETEST_HINT = "Mark a lab report as this incident's re-test (from the report, this step, or when uploading)";

/** Select a candidate report and mark it as the workflow incident's re-test. */
export function MarkRetest({ incidentId, onMarked }: { incidentId: string; onMarked?: () => void | Promise<void> }) {
  const [reports, setReports] = useState<LabReport[]>([]);
  const [facility, setFacility] = useState('');
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    setSelected(''); setDone(false); setError('');
    Promise.all([labIncidentsService.retestCandidates(incidentId), labIncidentsService.get(incidentId)])
      .then(([items, incident]) => { if (!cancelled) { setReports(items); setFacility(incident.facility_id); } })
      .catch(err => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [incidentId]);
  return <div onClick={event => event.stopPropagation()}>
    <small>{RETEST_HINT}</small>
    {done ? <p role="status">Report marked as re-test</p> : <>
      <label>Re-test report<select value={selected} onChange={event => setSelected(event.target.value)} disabled={busy}>
        <option value="">Select report</option>
        {reports.map(report => <option key={report.id} value={report.id}>{report.report_number || report.pdf_filename || report.id}</option>)}
      </select></label>
      <button className="btn btn-secondary" disabled={busy || !selected} onClick={async () => {
        setBusy(true); setError('');
        try { await labIncidentsService.linkRetest(incidentId, selected); setDone(true); await onMarked?.(); }
        catch (err) { setError((err as Error).message); } finally { setBusy(false); }
      }}>Mark as re-test</button>
    </>}
    {facility && <a href={`/lab-reports?facility_id=${facility}&retest_for=${incidentId}`}>Upload new report</a>}
    {error && <p role="alert">{error}</p>}
  </div>;
}

/** Offer eligible incidents on a parsed report, preserving upload context. */
export function ReportRetestCard({ report, canEdit, retestFor, onMarked }: {
  report: LabReportDetail; canEdit: boolean; retestFor?: string | null; onMarked: () => Promise<void>;
}) {
  const [incidents, setIncidents] = useState<RetestIncident[]>([]);
  const [marked, setMarked] = useState<RetestIncident | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    setIncidents([]); setMarked(null); setError('');
    if (!canEdit || report.incident || !['parsed', 'manual'].includes(report.parse_status)) return;
    labIncidentsService.retestIncidents(report.id)
      .then(items => { if (!cancelled) setIncidents(items); })
      .catch(err => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [report, canEdit]);
  const owner = marked || (report.incident?.lab_report_id && report.incident.lab_report_id !== report.id ? report.incident : null);
  const prefix = window.location.pathname.startsWith('/portal') ? '/portal' : '';
  if (owner) return <section className="lab-incident-panel">
    <p>Re-test for: <a href={`${prefix}/lab-reports/${owner.lab_report_id}`}>{owner.asset_name || 'Lab'} incident</a></p>
    {!report.human_verified_at && <p>Verify this report to allow closing the incident</p>}
  </section>;
  if (!incidents.length) return error ? <p role="alert">{error}</p> : null;
  return <section className="lab-incident-panel" aria-label="Is this a re-test?">
    <h2>Is this a re-test?</h2>
    {[...incidents].sort((a, b) => Number(b.id === retestFor) - Number(a.id === retestFor)).map(incident => <div key={incident.id}>
      <span>{incident.asset_name} · {analyteLabel(incident.analyte)} {incident.value?.toLocaleString()} {unitLabel(incident.unit || '')} · {new Date(incident.detected_date).toLocaleDateString('en-AU', {day:'numeric', month:'short'})}</span>{' '}
      {incident.id === retestFor && <strong>Selected incident · </strong>}
      <button className="btn btn-secondary" disabled={busy} onClick={async () => {
        setBusy(true); setError('');
        try { await labIncidentsService.linkRetest(incident.id, report.id); setMarked(incident); await onMarked(); }
        catch (err) { setError((err as Error).message); } finally { setBusy(false); }
      }}>Mark as re-test</button>
    </div>)}
    {error && <p role="alert">{error}</p>}
  </section>;
}
